// import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { UseQueryOptions, useQuery } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { CustomQueryOptions, FetchError } from 'src/types/TApiQueries';
import { getAllNotifications, getAssignedReviews } from '../functions/notificationsApi';
import { queryFnFetchErrorWrapper, throwFetchError } from '../utils/errorHandlers';
import { mergeQueryOptions } from '../utils/helpers';
import { queryNames } from '../utils/queryNames';

export const useAllNotificationsQuery = (
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<any[], FetchError>,
) => {
/*
  return {
    isLoading:false,
    refetch: () => {},
    data: {
      notifications: [],
    },
    isRefetching:false,
  }
*/
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(() => getAllNotifications(accessToken)), [accessToken]);

  const query = useQuery<any, FetchError>([queryNames.notification, 'all'], queryFn, actualQueryOptions);



  if (options?.handleErrors && query.status === 'error') {
    if (query.isRefetchError) {
      // Sentry.captureMessage('Failed refetching notifications', Sentry.Severity.Error);
    } else {
      throwFetchError(query.error);
    }
  }

  return query;

};

/**
 * Reviews the current user has assigned to other people.
 *
 * Separate query key from the inbox listing: same endpoint, different
 * question, and the two must not evict each other. Not polled — a page you
 * are looking at is refetched on focus, and an assigner does not need
 * second-by-second news about someone else's queue.
 */
export const useAssignedReviewsQuery = (
  openOnly = false,
  queryOptions?: UseQueryOptions<any, FetchError>,
) => {
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getAssignedReviews(accessToken, openOnly)),
    [accessToken, openOnly],
  );

  return useQuery<any, FetchError>(
    [queryNames.notification, 'assigned', openOnly],
    queryFn,
    queryOptions,
  );
};
